import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  User, 
  FileText, 
  BarChart3, 
  Link, 
  Image, 
  Grid, 
  Mail, 
  Trophy 
} from 'lucide-react';

interface WidgetPaletteProps {
  onClose: () => void;
  currentWidgets: string[];
  onAddWidget: (widgetId: string) => void;
}

const availableWidgets = [
  {
    id: 'profile-header',
    name: 'Profile Header',
    description: 'Avatar, name, username, and status',
    icon: User,
  },
  {
    id: 'bio',
    name: 'Bio',
    description: 'Description and favorite quote',
    icon: FileText,
  },
  {
    id: 'stats',
    name: 'Stats',
    description: 'Followers, following, ELO rating',
    icon: BarChart3,
  },
  {
    id: 'social-links',
    name: 'Social Links',
    description: 'Custom links and social media',
    icon: Link,
  },
  {
    id: 'banner',
    name: 'Banner',
    description: 'Banner image display',
    icon: Image,
  },
  {
    id: 'gallery',
    name: 'Gallery',
    description: 'Recent posts and bulletins',
    icon: Grid,
  },
  {
    id: 'contact',
    name: 'Contact',
    description: 'Contact information',
    icon: Mail,
  },
  {
    id: 'achievements',
    name: 'Achievements',
    description: 'Badges and achievements',
    icon: Trophy,
  },
];

export const WidgetPalette: React.FC<WidgetPaletteProps> = ({
  onClose,
  currentWidgets,
  onAddWidget,
}) => {
  const unusedWidgets = availableWidgets.filter(
    widget => !currentWidgets.includes(widget.id)
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Widgets</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
          {unusedWidgets.map((widget) => (
            <Card
              key={widget.id}
              className="p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => {
                onAddWidget(widget.id);
                onClose();
              }}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <widget.icon className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-sm">{widget.name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {widget.description}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        {unusedWidgets.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Grid className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>All available widgets are already added to your profile.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};